import React, { useState, useEffect, useRef } from 'react';
import { Paperclip, Send, X, Loader2 } from 'lucide-react';
import ChatBubble from './ChatBubble';
import { ChatMessage } from '../../api/types';
import {
  sendMessage,
  fetchMessages,
  markAsRead,
  uploadChatAttachment,
} from '../../api/chat';

interface ChatPanelProps {
  applicationId: string;
  currentUser: {
    uid: string;
    name: string;
    role: 'owner' | 'brfadmin';
  };
}

export default function ChatPanel({ applicationId, currentUser }: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Poll messages every 3 seconds
  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const msgs = await fetchMessages(applicationId);
        if (active) setMessages(msgs);
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      }
    };

    load();
    const interval = setInterval(load, 3000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [applicationId]);

  // Mark as read when messages change or panel opens
  useEffect(() => {
    if (messages.length > 0) {
      markAsRead(applicationId, currentUser.uid).catch(() => {});
    }
  }, [messages.length, applicationId, currentUser.uid]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const trimmedText = text.trim();
    if (!trimmedText && !pendingFile) return;

    setSending(true);
    setError(null);

    try {
      let attachment: ChatMessage['attachment'] | undefined;

      if (pendingFile) {
        attachment = await uploadChatAttachment(applicationId, pendingFile);
      }

      await sendMessage(applicationId, {
        text: trimmedText,
        senderUid: currentUser.uid,
        senderName: currentUser.name,
        senderRole: currentUser.role,
        attachment,
      });

      setText('');
      setPendingFile(null);

      // Immediately fetch to show the sent message
      const updated = await fetchMessages(applicationId);
      setMessages(updated);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err: any) {
      setError(err.message || 'Kunde inte skicka meddelande');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/png',
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Filtypen stöds inte. Tillåtna: PDF, DOCX, PPTX, JPEG, PNG');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Filen är för stor. Max 10 MB.');
      return;
    }

    setPendingFile(file);
    setError(null);
    // Reset file input
    e.target.value = '';
  };

  const removePendingFile = () => {
    setPendingFile(null);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-[#626262]">Inga meddelanden än. Skriv det första!</p>
          </div>
        )}
        {messages.map((msg) => (
          <ChatBubble
            key={msg.id}
            message={msg}
            isOwn={msg.senderUid === currentUser.uid}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-xs text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Pending file preview */}
      {pendingFile && (
        <div className="mx-4 mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[#62B8E9]" />
          <span className="text-xs text-[#222222] truncate flex-1">{pendingFile.name}</span>
          <span className="text-xs text-[#626262]">
            {(pendingFile.size / 1024).toFixed(0)} KB
          </span>
          <button onClick={removePendingFile} className="text-[#626262] hover:text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-[#E2EAF0] p-4">
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.docx,.pptx,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-lg hover:bg-gray-100 text-[#626262] transition-colors flex-shrink-0"
            title="Bifoga fil"
            disabled={sending}
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <div className="flex-1 bg-[#F9FCFF] border border-[#E2EAF0] rounded-xl px-4 py-3 focus-within:ring-2 focus-within:ring-[#62B8E9] focus-within:border-transparent transition-all">
            <textarea
              ref={textareaRef}
              rows={1}
              placeholder="Skriv ett meddelande..."
              className="w-full bg-transparent text-sm text-[#222222] placeholder-[#626262] resize-none focus:outline-none"
              style={{ maxHeight: '120px' }}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              onKeyDown={handleKeyDown}
              disabled={sending}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={sending || (!text.trim() && !pendingFile)}
            className="p-2.5 rounded-xl text-white transition-colors flex-shrink-0 disabled:opacity-50"
            style={{ backgroundColor: '#62B8E9' }}
            onMouseEnter={(e) => {
              if (!(e.target as HTMLButtonElement).disabled)
                (e.target as HTMLElement).style.backgroundColor = '#4A9FD1';
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.backgroundColor = '#62B8E9';
            }}
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-[#626262] mt-2">PDF, DOCX, PPTX, JPEG, PNG (max 10 MB)</p>
      </div>
    </div>
  );
}
